/**
 * 客人加收服務費的共用計算
 *
 * ⚠️ 這是 beauty-server/utils/paymentSurcharge.js 的複本，
 *    除了最下方的匯出方式之外，內容必須逐字一致。改動時兩邊要一起改。
 * ─────────────────────────────────────────────────────────────────
 *
 * 【這個檔案為什麼要獨立出來】
 *
 * 服務費的數字會同時出現在三個地方：結帳頁的即時預覽、後端建單的驗證、
 * 客人端的消費紀錄。只要三者有任何一處算法不同，就會出現
 * 「畫面顯示 289、訂單存 288」這種對不起來的狀況，而且是靜默發生的。
 *
 * 所以公式只准有一份。前端 beauty-business/src/utils/paymentSurcharge.js
 * 是這支的複本（只改 module 匯出方式），兩邊修改必須同步。
 *
 *
 * 【為什麼預設用 gross_up 而不是 add_on】
 *
 * 收單行是對「客人實際刷的總額」抽成，不是對原本的應收抽成。
 * 以應收 10,000、費率 2.8% 為例：
 *
 *   add_on   服務費 = 10,000 × 2.8% = 280
 *            客人刷 10,280 → 銀行扣 10,280 × 2.8% = 287.84 → 店家實收 9,992.16
 *            → 店家仍短收 7.84（也就是 基數 × 費率²）
 *
 *   gross_up 總額 = 10,000 / (1 − 2.8%) = 10,288.07 → 進位 10,289
 *            服務費 = 289，銀行扣 288.09 → 店家實收 10,000.91 ✅
 *
 * 兩種都保留是因為有些店家就是要「看得懂的 2.8%」，寧可少收那幾塊錢。
 *
 *
 * 【安全預設】
 *
 * 預設 feeBearer='shop'、applyToCard/applyToMobile 皆為 false ——
 * 設定讀不到或欄位缺漏時，絕不會擅自向客人加收費用。
 */

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const round2 = (v) => Math.round(num(v) * 100) / 100

/**
 * 把 business.paymentFeeSettings 正規化成一份可直接使用的設定。
 *
 * 存在的理由有兩個：
 *   1. 舊商家沒有這些欄位，散落各處的 `?? default` 遲早會不一致
 *   2. 未來若要支援「分店各自設定」，只要改這一個函式的 override 來源，
 *      不必回頭改每個呼叫點（目前 CheckoutOrder 沒有分店欄位，尚無法分店）
 *
 * @param {object} business  Business document 或 lean object
 * @param {object} [override] 未來的分店層設定，會覆蓋商家層同名欄位
 */
const resolvePaymentFeeSettings = (business, override = null) => {
  const base = (business && business.paymentFeeSettings) || {}
  const s = override ? { ...base, ...override } : base

  const bearer = s.feeBearer === 'customer' ? 'customer' : 'shop'

  return {
    cardFeeRate: s.cardFeeRate != null ? num(s.cardFeeRate) : 2.8,
    mobilePaymentFeeRate:
      s.mobilePaymentFeeRate != null ? num(s.mobilePaymentFeeRate) : 2.5,
    autoCalculate: s.autoCalculate !== false,

    feeBearer: bearer,
    feeBearerMode: s.feeBearerMode === 'add_on' ? 'add_on' : 'gross_up',
    feeRounding: s.feeRounding === 'round' ? 'round' : 'ceil',
    feeLabel: (s.feeLabel && String(s.feeLabel).trim()) || '服務費',
    // 只有在 feeBearer='customer' 時這兩個開關才有意義；
    // 這裡先 AND 起來，呼叫端就不必每次都判斷兩層
    applyToCard: bearer === 'customer' && s.applyToCard === true,
    applyToMobile: bearer === 'customer' && s.applyToMobile === true
  }
}

/**
 * 這間店現在有沒有真的在收服務費。
 * 兩個 applyTo 都關 = 等同沒開，結帳流程可以整段跳過。
 */
const isSurchargeEnabled = (settings) =>
  !!(settings && (settings.applyToCard || settings.applyToMobile))

/**
 * 單一支付方式的服務費。
 *
 * @param {number} base     該支付方式的基數（**尚未含服務費**的金額）
 * @param {number} ratePct  費率，百分比（2.8 代表 2.8%）
 * @param {'add_on'|'gross_up'} mode
 * @param {'ceil'|'round'} rounding
 * @returns {number} 服務費金額（≥ 0）
 */
const calcOneSurcharge = (base, ratePct, mode, rounding) => {
  const b = round2(base)
  const r = num(ratePct) / 100

  // 費率 ≥ 100% 時 gross_up 會除以 0 或負數，直接視為不收，避免算出天文數字
  if (b <= 0 || r <= 0 || r >= 1) return 0

  const roundFn = rounding === 'round' ? Math.round : Math.ceil

  const fee =
    mode === 'add_on'
      ? roundFn(b * r)
      : roundFn(b / (1 - r)) - b

  return Math.max(0, round2(fee))
}

/**
 * 依刷卡／行動支付的基數算出這張單要向客人收的服務費。
 *
 * ⚠️ 傳入的必須是「未含服務費」的基數。
 *    也就是：應收扣掉現金、儲值金、優惠券、定金等其他付款方式之後，
 *    真正要走電子支付的那部分。對整張應收算會多收。
 *
 * @returns {{ total:number, card:number, mobile:number }}
 */
const calcCustomerSurcharge = ({ cardBase = 0, mobileBase = 0, settings }) => {
  const s = settings || {}
  if (!isSurchargeEnabled(s)) return { total: 0, card: 0, mobile: 0 }

  const card = s.applyToCard
    ? calcOneSurcharge(cardBase, s.cardFeeRate, s.feeBearerMode, s.feeRounding)
    : 0

  const mobile = s.applyToMobile
    ? calcOneSurcharge(
        mobileBase,
        s.mobilePaymentFeeRate,
        s.feeBearerMode,
        s.feeRounding
      )
    : 0

  return { total: round2(card + mobile), card, mobile }
}

export {
  resolvePaymentFeeSettings,
  isSurchargeEnabled,
  calcOneSurcharge,
  calcCustomerSurcharge
}
